//notificationtable.jsx
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TablePagination,
} from "@mui/material";
import ActionButtons from "./NotificationAction";
import PreviewModal from "./actions/PreviewModal";
import RevokeConfirmationModal from "./actions/RevokeModal";
import { useGlobalConfig } from "context/GlobalContext";
import { toast } from "react-toastify";
import "./NotificationStyles.scss";

const NotificationTable = ({
  notifications,
  selectedNotifications,
  setSelectedNotifications,
  currentPage,
  totalNotifications,
  notificationsPerPage,
  onPageChange,
  pilotId,
  handleFetchNotifications,
  setNotificationsPerPage,
  setCurrentPage
}) => {
  const { rc } = useGlobalConfig();
  const [previewContent, setPreviewContent] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [openRevoke, setOpenRevoke] = useState(false);
  const [notificationToRevoke, setNotificationToRevoke] = useState(null);
  const [notifcationFocus , setNotificationFocus] = useState(null);
  const [focusIndex, setFocusIndex] = useState(null);

  const handleSelectNotification = (notification) => {
    setSelectedNotifications((prev) =>
      prev.includes(notification)
        ? prev.filter((id) => id !== notification)
        : [...prev, notification]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedNotifications(notifications);
    } else {
      setSelectedNotifications([]);
    }
  };

  const handlePreview = async (notification) => {
    console.log("Preview Modal Opened"); // Debugging log
    const previewContent = await fetchNotificationHtml(notification.notificationId);
    setPreviewContent(previewContent);
    setNotificationFocus(notification);
    const index = notifications.findIndex(noti => noti.notificationId === notification.notificationId);
    setFocusIndex(index);
    setOpenPreview(true);
  };

  const handleNotificationChangeFromPreview = (notificationIndex) => {
    console.log(notificationIndex)
    const nextNotification = notifications[notificationIndex];
    console.log(nextNotification)
    handlePreview(nextNotification);
  }

  const fetchNotificationHtml = async (notificationId) => {
    const finalUrl = `/2.1/utility_notifications/notifications/${notificationId}`;
    const response = await rc.apiClient.get(finalUrl);
    return response.data;
  };

  const handleRevoke = (notificationData) => {
    console.log("Revoke Modal Opened"); // Debugging log
    setNotificationToRevoke(notificationData);
    setOpenRevoke(true);
  };

  const confirmRevoke = async () => {
    const notificationsToBeRevoked = [];
    if (notificationToRevoke) {
      notificationsToBeRevoked.push(notificationToRevoke);
    } else if (selectedNotifications.length > 0) {
      notificationsToBeRevoked.push(...selectedNotifications);
    }

    try {
      const url = `/2.1/utility_notifications/${pilotId}/revoke-notifications`;
      console.log("Final Revoke URL:", url);
      const response = await rc.apiClient.put(url, notificationsToBeRevoked);
      if (response.error) {
        throw new Error("Error in revoking notifications");
      }
      toast.success("Notifications revoked successfully");
    } catch (error) {
      toast.error("Error in revoking notifications");
    } finally {
      setOpenRevoke(false);
      setNotificationToRevoke(null);
      setSelectedNotifications([]); // Clear selected notifications after revoking
      handleFetchNotifications(); // Refresh the notifications list
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        onClick={() => setOpenRevoke(true)}
        disabled={selectedNotifications.length === 0}
      >
        Revoke Selected
      </Button>

      {/* Pagination Controls */}
      <TablePagination
        component="div"
        // count={totalNotifications == notificationsPerPage ? -1 : totalNotifications} // Total number of notifications
        count={totalNotifications || 0} // Default to 0 if undefined
        rowsPerPage={notificationsPerPage}
        page={currentPage - 1} // Convert to 0-based index
        onPageChange={(event, newPage) => {console.log(newPage);onPageChange(newPage+1);}} // Convert back to 1-based index
        rowsPerPageOptions={[10,50,100,500]}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setNotificationsPerPage(newRowsPerPage); // Update the rows per page state
          setCurrentPage(1); // Reset to the first page
        }} 
      />


      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    selectedNotifications?.length === notifications.length &&
                    notifications.length > 0
                  }
                  onChange={handleSelectAll}
                  color="primary"
                />
              </TableCell>
              <TableCell className="bold-header">Notification ID</TableCell>
              <TableCell className="bold-header">User ID</TableCell>
              <TableCell className="bold-header">Notification Type</TableCell>
              <TableCell className="bold-header">Delivery Destination</TableCell>
              <TableCell className="bold-header">Scheduled Time</TableCell>
              <TableCell className="bold-header">Notification Title</TableCell>
              <TableCell className="bold-header">Status</TableCell>
              <TableCell className="bold-header">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(notifications) && notifications.map((notification, index) => (
              <TableRow 
                key={notification.notificationId} 
                className={index % 2 === 0 ? 'even-row' : 'odd-row'}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedNotifications.includes(notification)}
                    onChange={() => handleSelectNotification(notification)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>{notification.notificationId}</TableCell>
                <TableCell>{notification.userId}</TableCell>
                <TableCell>{notification.notificationType}</TableCell>
                <TableCell>{notification.deliveryDestination}</TableCell>
                <TableCell>
                  {notification.eventData?.scheduled_time ? new Date(notification.eventData.scheduled_time * 1000).toLocaleString() : ""}
                </TableCell>
                <TableCell>{notification.notificationTitle}</TableCell>
                <TableCell>{notification.status}</TableCell>
                <TableCell>
                  <ActionButtons
                    notificationData={notification}
                    onPreview={handlePreview}
                    onRevoke={handleRevoke}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>

        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <TablePagination
        component="div"
        count={totalNotifications === notificationsPerPage ? -1 : totalNotifications} // Total number of notifications
        rowsPerPage={notificationsPerPage}
        page={currentPage - 1} // Convert to 0-based index
        onPageChange={(event, newPage) => {console.log(newPage);onPageChange(newPage+1);}} // Convert back to 1-based index
        rowsPerPageOptions={[10,50,100,500]}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setNotificationsPerPage(newRowsPerPage); // Update the rows per page state
          setCurrentPage(1); // Reset to the first page
        }} 
      />


      {/* Preview Modal */}
      {openPreview && <PreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        content={previewContent}
        notification={notifcationFocus}
        notificationsPerPage={notificationsPerPage}
        focusIndex={focusIndex}
        changePreviewNotification={handleNotificationChangeFromPreview}
        totalNotifications={totalNotifications}
      />}

      {/* Revoke Confirmation Modal */}
      {openRevoke && <RevokeConfirmationModal
        open={openRevoke}
        onClose={() => setOpenRevoke(false)}
        onConfirm={confirmRevoke}
        notificationIds={selectedNotifications} // Pass selected notification IDs to the modal if needed
      />}

    </>
  );
};

export default NotificationTable;
